<script setup lang="ts">
import { ref, computed } from 'vue';
import Btn from '../Btn/Btn.vue';
import SelectBox from '../SelectBox/SelectBox.vue';
import BtnIcon from '../BtnIcon/BtnIcon.vue';
import InputField from '../InputField/InputField.vue';

const LIMIT_OPTIONS = [
	{ value: 10, label: '10' },
	{ value: 25, label: '25' },
	{ value: 50, label: '50' },
];

const props = defineProps<{
	currentLimit: string | number | null;
	currentPage: string | number | null;
	disabledLimitSelector?: boolean;
	goToPageLabel: string;
	itemsPerPageLabel: string;
	totalNumberOfItems: string | number | null;
}>();

const emit = defineEmits<{ (e: 'change', payload: { page: number; limit: number }): void }>();

const pageInput = ref(1);

const computedNumberOfPages = computed(() => {
	return Math.ceil(Number(props.totalNumberOfItems) / Number(props.currentLimit));
});

const handleChange = (selectedPage: number, selectedLimit: number) => {
	if (selectedPage > computedNumberOfPages.value || selectedPage <= 0) return;

	emit('change', { page: selectedPage, limit: selectedLimit });
};
</script>
<template>
	<div class="width-100 flex items-center justify-between" data-ts="footer-pagination">
		<div class="flex items-center">
			<select-box
				dropdown-position-top
				data-ts="footer-pagination--select-box"
				:options="LIMIT_OPTIONS"
				:disabled="disabledLimitSelector"
				:selected-value="currentLimit"
				@select="handleChange(1, $event)"
			/>
			<span class="ml-2" data-ts="items-per-page">{{ itemsPerPageLabel }}</span>
		</div>
		<div class="flex items-center">
			<p class="ml-6 mr-2">{{ props.goToPageLabel }}</p>
			<InputField
				style="width: 60px"
				:placeholder="currentPage?.toString() || '1'"
				type="number"
				@input="pageInput = $event"
				@keydown.enter="handleChange(pageInput, currentLimit)"
			/>
			<Btn
				variant="secondary"
				label="Go"
				class="ml-2 mr-8"
				@click="handleChange(pageInput, currentLimit)"
			/>
			<BtnIcon
				ariaLabel="Go to previous page"
				icon-name="bx-chevron-left"
				variant="secondary"
				:disabled="currentPage === 1"
				@click="handleChange(Number(currentPage) - 1, currentLimit)"
			/>
			<p class="mx-4">
				<span class="text-weight-bold">{{ currentPage }}</span> /
				{{ computedNumberOfPages }}
			</p>
			<BtnIcon
				ariaLabel="Go to next page"
				icon-name="bx-chevron-right"
				variant="secondary"
				data-test="next-page-btn"
				:disabled="currentPage === computedNumberOfPages"
				@click="handleChange(Number(currentPage) + 1, currentLimit)"
			/>
		</div>
	</div>
</template>
